/**
 * PayrollPage.jsx — reimagined around two worlds:
 *
 *   👥 CREW ACCOUNTING  (default) — paying people: log work, who's owed, mark paid.
 *   🏢 COMPANY ACCOUNTING          — the business's books (P&L, company expenses). Coming next.
 *
 * Admins toggle between the two. Non-admin crew only ever see Crew Accounting
 * (their own log-first view), so the toggle is hidden for them.
 */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Typography, ToggleButtonGroup, ToggleButton, Paper } from '@mui/material';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import { useRBAC } from '../hooks/useRBAC';
import CrewAccounting from '../components/payroll/CrewAccounting';
import ExpenseList from '../components/expenses/ExpenseList';
import StaticExpenseList from '../components/expenses/StaticExpenseList';
import ExpenseForm from '../components/expenses/ExpenseForm';
import StaticExpenseForm from '../components/expenses/StaticExpenseForm';
import BankPanel from '../components/bank/BankPanel';

// Company Accounting = the business's books: recurring/static burn (default),
// one-off variable expenses, and the bank feed those expenses can come from.
function CompanyAccounting({ newExpense = false }) {
  const [activeTab, setActiveTab] = useState('recurring');
  const [showForm, setShowForm] = useState(false);

  // Ctrl+K 'Add Expense' lands here with the variable-expense form open.
  useEffect(() => {
    if (newExpense) { setActiveTab('variable'); setShowForm(true); }
  }, [newExpense]);

  return (
    <div className="flex flex-col gap-3 h-full">
      <ToggleButtonGroup
        size="small"
        value={activeTab}
        exclusive
        onChange={(_, v) => { if (v) { setActiveTab(v); setShowForm(false); } }}
      >
        <ToggleButton value="recurring" sx={{ textTransform: 'none', px: 2 }}>Recurring</ToggleButton>
        <ToggleButton value="variable" sx={{ textTransform: 'none', px: 2 }}>Variable</ToggleButton>
        <ToggleButton value="bank" sx={{ textTransform: 'none', px: 2 }}>Bank</ToggleButton>
      </ToggleButtonGroup>

      {showForm && activeTab === 'variable' && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="overline" sx={{ mb: 2, display: 'block' }}>New Expense</Typography>
          <ExpenseForm onSave={() => setShowForm(false)} onCancel={() => setShowForm(false)} />
        </Paper>
      )}
      {showForm && activeTab === 'recurring' && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="overline" sx={{ mb: 2, display: 'block' }}>New Recurring Expense</Typography>
          <StaticExpenseForm onSave={() => setShowForm(false)} onCancel={() => setShowForm(false)} />
        </Paper>
      )}

      <Box sx={{ flex: 1, minHeight: 0 }}>
        {activeTab === 'recurring' && <StaticExpenseList onNewExpense={() => setShowForm(true)} />}
        {activeTab === 'variable' && <ExpenseList onNewExpense={() => setShowForm(true)} />}
        {activeTab === 'bank' && <BankPanel />}
      </Box>
    </div>
  );
}

export default function PayrollPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasAccess } = useRBAC();
  const isAdmin = hasAccess('payroll', 'full');

  // Deep-link action (?action=addExpense) → Company Accounting's new-expense form.
  const action = searchParams.get('action');
  const newExpense = isAdmin && action === 'addExpense';

  const urlSection = searchParams.get('section');
  const section = isAdmin && (urlSection === 'company' || newExpense) ? 'company' : 'crew';
  const setSection = (s) => {
    const next = new URLSearchParams(searchParams);
    next.set('section', s);
    setSearchParams(next, { replace: true });
  };

  // Consume the action param so it doesn't re-fire on later navigation.
  useEffect(() => {
    if (!action) return;
    const next = new URLSearchParams(searchParams);
    next.delete('action');
    next.set('section', section);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action]);

  return (
    <Box data-track-area="payroll" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h5" sx={{ fontFamily: '"Bebas Neue", sans-serif', letterSpacing: 1 }}>
          {isAdmin ? 'Payroll' : 'My Pay'}
        </Typography>
        {isAdmin && (
          <ToggleButtonGroup size="small" exclusive value={section} onChange={(_, v) => v && setSection(v)}>
            <ToggleButton value="crew" sx={{ textTransform: 'none', px: 2, gap: 0.5 }}>
              <GroupsRoundedIcon sx={{ fontSize: 18 }} /> Crew Accounting
            </ToggleButton>
            <ToggleButton value="company" sx={{ textTransform: 'none', px: 2, gap: 0.5 }}>
              <BusinessRoundedIcon sx={{ fontSize: 18 }} /> Company Accounting
            </ToggleButton>
          </ToggleButtonGroup>
        )}
      </Box>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        {section === 'company'
          ? <CompanyAccounting newExpense={newExpense} />
          : <CrewAccounting />}
      </Box>
    </Box>
  );
}
